// @ts-nocheck
/**
 * Replay-only live map container — no selectionStore, no brush, no seek-by-click.
 * Used by LiveMap explore; fleet/day maps use MapContainer instead.
 */
import { createSignal, createEffect, onMount, onCleanup, Show, untrack, createMemo } from "solid-js";
import { createStore } from "solid-js/store";

let mapboxgl: any = null;

import Loading from "../../utilities/Loading";
import PlayPause from "../../utilities/PlayPause";
import MapSettings from "../../menus/MapSettings";
import LiveMapUPlotTimeSeries from "./LiveMapUPlotTimeSeries";
import { buildLiveFrameSnapshot } from "../../../utils/liveMapFrameSnapshot";
import { isRenderYieldActive } from "../../../utils/renderYield";
import { collectBoatWindSamples } from "../../../utils/boatWindSamples";
import { collectBoatHistorySamples } from "./overlays/windField/windSampleHistory";
import { liveReplayFilteredDataOptions } from "../../../utils/liveReplayFilteredData";
import {
  buildLiveReplayFramePoints,
  buildLiveReplaySourceStyles,
  livePointCoords,
} from "../../../utils/liveReplayFramePoints";
import { error as logError, debug as logDebug, warn as logWarn } from "../../../utils/console";
import { registerActiveComponent, unregisterActiveComponent } from "../../../pages/Dashboard";

import LiveTrackLayer from "../map/components/LiveTrackLayer";
import LiveBspTable, { isLiveDefaultDataOverlay } from "./components/LiveBspTable";
import LiveMapStreamStatus from "./components/LiveMapStreamStatus";
import LiveTwaTackTable from "./components/LiveTwaTackTable";
import LiveMultiBoatLayer from "../map/components/LiveMultiBoatLayer";
// GPU/columnar live-map path (flag-gated by config.LIVE_MAP_GPU)
import GlTrackLayer from "../map/components/gl/GlTrackLayer";
import GlBoatLayer from "../map/components/gl/GlBoatLayer";
import { arrowStreamClient } from "../../../services/arrowStreamClient";
import { liveColumnarStore } from "../../../store/liveColumnarStore";
import { hydrateSourceBack } from "../../../store/liveMapHydration";
import { retainHighlightedSources } from "../../../store/liveTrackHighlightStore";
import { LIVE_STREAM_CHANNELS } from "../../../constants/liveChannels";
import RaceCourseLayer from "../map/components/RaceCourseLayer";
import WindArrow from "../map/components/WindArrow";
import AlarmsExploreMarkersLayer from "../map/components/AlarmsExploreMarkersLayer";

import { initializeOverlays } from "./overlays";
import OverlayManager from "./overlays/OverlayManager";

import { streamingStore } from "../../../store/streamingStore";
import { streamingService } from "../../../services/streamingService";
import { sourcesStore } from "../../../store/sourcesStore";
import { defaultChannelsStore } from "../../../store/defaultChannelsStore";
import { persistantStore } from "../../../store/persistantStore";
import { config } from "@config/env";
import {
  selectedTime,
  isPlaying,
  setIsPlaying,
  timeWindow,
  setTimeWindow,
  smoothPlaybackTimeForTrack,
  smoothPlaybackTimeForBoat,
  syncSelectedTimeManual,
  startPeriodicSync,
  setLiveMode,
  requestTimeControl,
  getDisplayWindowReferenceTime,
  initializeIsPlayingFollowEffect,
  setSelectedTime,
  setIsManualTimeChange,
  getLiveDisplayTargetMs,
  snapLiveSmoothPlaybackToMs,
  isLiveReplayPlayheadReady,
  liveFollow,
} from "../../../store/playbackStore";
import { liveConfigStore } from "../../../store/liveConfigStore";
import { streamingStatusStore } from "../../../store/streamingStatusStore";
import { setHasChartsWithOwnFilters } from "../../../store/filterStore";
import { logPageLoad } from "../../../utils/logging";

import "../../../styles/thirdparty/mapbox-gl.css";
import { noteLiveMapMounted, noteLiveMapUnmounted, liveMapsMountedHere, mayClearGlobalLiveMode, isLivePageActive } from '../../../utils/liveScope';

interface LiveMapContainerProps {
  objectName?: string;
  dataSourcesOptions?: any[] | (() => any[]);
  [key: string]: any;
}

const LIVE_MAP_DEFAULT_TIME_WINDOW_MIN = 2;
const DEFAULT_ZOOM_THRESHOLD = 16;
/** Brief world view while waiting for boat GPS — never use NYC as the live map default. */
const LIVE_MAP_PLACEHOLDER_CENTER: [number, number] = [0, 20];
const LIVE_MAP_PLACEHOLDER_ZOOM = 2;
const LIVE_MAP_INIT_WAIT_MS = 2500;
const LEGACY_NYC_CENTER = { lng: -74.006, lat: 40.7128 };

export default function LiveMapContainer(props: LiveMapContainerProps) {
  const objectName = props?.objectName || "map_default";

  const [isSourcesReady, setIsSourcesReady] = createSignal(false);
  const [hasShownMapOnce, setHasShownMapOnce] = createSignal(false);
  createEffect(() => {
    if (sourcesStore.isReady()) setIsSourcesReady(true);
  });

  createEffect(() => {
    registerActiveComponent("map");
    // onCleanup, not a returned function: Solid discards createEffect return values, so 'map' was
    // never removed from the global activeComponents set (pages/Dashboard.tsx:28-49) and the
    // selection banner stayed suppressed on other pages for the rest of the session. Safe to fire
    // per re-run too — this effect reads nothing tracked, so it only runs once.
    onCleanup(() => unregisterActiveComponent("map"));
  });

  const [mapContainer, setMapContainer] = createSignal<HTMLDivElement | null>(null);
  let chartContainer: HTMLElement | null = null;
  const [map, setMap] = createSignal<any>(null);
  const [currentZoom, setCurrentZoom] = createSignal<number>(14);
  const [zoomSettings, setZoomSettings] = createSignal<{ race_course?: number; boats?: number; maneuvers?: number } | null>(null);

  const raceCourseZoomThreshold = createMemo(() => {
    const s = zoomSettings();
    return s?.race_course !== undefined ? s.race_course : DEFAULT_ZOOM_THRESHOLD;
  });
  // Live map: always show boat icons when the tracks overlay is on (size scales with zoom in LiveMultiBoatLayer).
  const showBoats = createMemo(() => true);

  const { twsName, twdName, latName, lngName } = defaultChannelsStore;

  const dataSourcesOptionsMemo = createMemo(() => {
    const options = props?.dataSourcesOptions;
    return typeof options === "function" ? options() : options || [];
  });

  const selectedSourceIds = createMemo(() => {
    const liveOptions = dataSourcesOptionsMemo();
    const ids = new Set<number>();
    for (const opt of liveOptions) {
      if (opt.signal?.[0]?.()) {
        const m = opt.key?.match(/source-(\d+)/);
        if (m) ids.add(Number(m[1]));
      }
    }
    return ids;
  });

  createEffect(() => {
    setLiveMode(true);
    requestTimeControl("playback");
  });
  // Registered on the COMPONENT's owner, not inside the effect above, so it fires once on unmount.
  //
  // The disposer this replaces was returned from the effect, which Solid discards — so it never ran
  // at all, and liveMode() stayed true after leaving the live map. The module-global playback
  // interval then kept taking its live branch on whatever page was opened next: a history pull and the
  // sequential per-boat /stream/status chain, every 2s, for the rest of the session.
  //
  // Deliberately NOT `onCleanup` inside the effect. `requestTimeControl()` reads activeComponent()
  // and isManualTimeChange() without untrack, so that effect re-runs on every seek and pause; a
  // cleanup registered there would toggle setLiveMode(false) -> setLiveMode(true) per re-run, and
  // the `if (!wasLive)` follow re-arm in playbackStore would force live-follow back on, clobbering a
  // manual pause. At component scope there is exactly one teardown and no re-run to trip over.
  //
  // The clear is now REFCOUNTED and scoped to this tab. `liveMode` is a createSyncSignal, so
  // `setLiveMode(false)` reaches every window, while the count of live maps that decides to call it
  // is module-level and therefore per-tab. Unguarded, this unmount turned live mode off for a live
  // report page in another window — or for the live page this tab was navigating TO — leaving the
  // Follow toggle reading LIVE over dead merge gates, recoverable only by reloading. See
  // utils/liveScope.ts.
  noteLiveMapMounted();
  onCleanup(() => {
    noteLiveMapUnmounted();
    if (mayClearGlobalLiveMode(liveMapsMountedHere(), isLivePageActive())) {
      setLiveMode(false);
    }
  });

  /** Anchor playhead slightly behind the live cap so playback has runway between WS batches. */
  let liveReplayPlayheadAnchored = false;

  const PLAYHEAD_DRIFT_THRESHOLD_MS = 30_000;
  const LIVE_PLAYHEAD_PREROLL_MS = 2_000;

  onMount(() => {
    try {
      initializeIsPlayingFollowEffect();
    } catch (err) {
      logWarn("LiveMapContainer: initializeIsPlayingFollowEffect failed", err);
    }
    streamingStatusStore.initialize();
    void liveConfigStore.fetchLiveConfig();
  });

  createEffect(() => {
    const status = streamingStatusStore.status();
    liveConfigStore.syncLiveConfigFromStatus({
      streamingStarted: status.streamingStarted,
      simulationActive: status.simulationActive,
      isLiveProcessingActive: status.isLiveProcessingActive,
    });
  });

  createEffect(() => {
    if (liveReplayPlayheadAnchored) return;
    const ids = selectedSourceIds();
    if (ids.size === 0) return;
    trackLiveDataChange();
    // Is there a live data edge yet? Columnar store in gpuMode (streamingStore idle), else streamingStore.
    const latest = gpuMode()
      ? (liveColumnarStore.latestTs(selectedFrameIds()) ?? 0)
      : streamingStore.getLatestTimestamp(ids);
    if (!latest || latest <= 0) return;

    const targetMs = getLiveDisplayTargetMs(ids);
    if (!isLiveReplayPlayheadReady(targetMs)) return;
    const startMs = Math.max(0, targetMs - LIVE_PLAYHEAD_PREROLL_MS);

    setIsManualTimeChange(false);
    setSelectedTime(new Date(startMs), "playback", true);
    snapLiveSmoothPlaybackToMs(startMs);
    liveReplayPlayheadAnchored = true;

    if (!isPlaying()) {
      setIsPlaying(true);
    }
    logDebug("LiveMapContainer: painted playhead behind live window edge", { startMs, targetMs, latest });
  });

  /** While FOLLOWING, snap playhead when it drifts far from the live data edge. */
  createEffect(() => {
    // `liveFollow` first, and not just `isPlaying`: a scrub stops the clock, but the live tick
    // re-arms `isPlaying` within ~2s whenever the playhead sits near the edge (playbackStore's
    // auto-resume). Without this guard that brief re-arm let the drift correction below fire and
    // yank a deliberate scrub straight back to the live edge. Follow is the user's intent and
    // nothing re-arms it behind their back, so it is the correct gate for "chase the edge".
    if (!liveFollow()) return;
    if (!isPlaying()) return;
    const ids = selectedSourceIds();
    if (ids.size === 0) return;
    trackLiveDataChange();
    const targetMs = getLiveDisplayTargetMs(ids);
    if (!isLiveReplayPlayheadReady(targetMs)) return;
    const currentMs = selectedTime().getTime();
    if (!Number.isFinite(currentMs)) return;
    const drift = Math.abs(currentMs - targetMs);
    if (drift > PLAYHEAD_DRIFT_THRESHOLD_MS) {
      const correctedMs = Math.max(0, targetMs - LIVE_PLAYHEAD_PREROLL_MS);
      setSelectedTime(new Date(correctedMs), "playback", true);
      snapLiveSmoothPlaybackToMs(correctedMs);
      logDebug("LiveMapContainer: corrected playhead drift", { currentMs, correctedMs, targetMs, drift });
    }
  });

  let liveDefaultTimeWindowApplied = false;
  createEffect(() => {
    if (liveDefaultTimeWindowApplied) return;
    liveDefaultTimeWindowApplied = true;
    if (timeWindow() === 0) {
      setTimeWindow(LIVE_MAP_DEFAULT_TIME_WINDOW_MIN);
      logDebug("LiveMapContainer: default time window", { minutes: LIVE_MAP_DEFAULT_TIME_WINDOW_MIN });
    }
  });

  let lastInitializedSourceIds = new Set<number>();
  createEffect(() => {
    // Phase D4: in gpuMode the columnar path (arrowStreamClient → liveColumnarStore) drives ALL rendering,
    // map-fit, playhead, and status — the legacy streamingStore + JSON WS are no longer initialized. (Map-fit
    // now collects GPS from the columnar store; see collectLiveGpsPointsForBounds.)
    if (gpuMode()) return;
    void streamingStore.getStreamingLifecycleEpoch()();
    if (!config.ENABLE_WEBSOCKETS) return;
    const sourceIds = selectedSourceIds();
    if (!sourceIds || sourceIds.size === 0) return;

    const sourceIdsArray = Array.from(sourceIds);
    const sourcesChanged =
      lastInitializedSourceIds.size !== sourceIds.size ||
      sourceIdsArray.some((id) => !lastInitializedSourceIds.has(id)) ||
      Array.from(lastInitializedSourceIds).some((id) => !sourceIds.has(id));

    const staleConnection =
      streamingStore.isInitialized && !streamingService.isConnected();

    if (staleConnection) {
      logDebug("[LiveMapContainer] Streaming marked initialized but WebSocket is down — reconnecting");
      streamingStore.cleanup();
      lastInitializedSourceIds = new Set();
    }

    if (!streamingStore.isInitialized) {
      streamingStore
        .initialize(sourceIds)
        .catch((err) => logError("[LiveMapContainer] streaming init failed:", err))
        .then(() => {
          lastInitializedSourceIds = new Set(sourceIds);
        });
    } else if (sourcesChanged) {
      streamingStore.updateSelectedSources(sourceIds, lastInitializedSourceIds).catch((err) => {
        logError("[LiveMapContainer] updateSelectedSources failed:", err);
      });
      lastInitializedSourceIds = new Set(sourceIds);
    }
  });

  createEffect(() => {
    if (isSourcesReady()) setHasShownMapOnce(true);
  });

  // GPU/columnar live-map path (flag-gated). Streams binary Arrow deltas into liveColumnarStore, which the
  // GL boat/track layers read. Runs ALONGSIDE the legacy path during transition (overlays/table/timeline
  // still read streamingStore); those migrate to the columnar store in a later step.
  const gpuMode = createMemo(() => config.LIVE_MAP_GPU === true);
  createEffect(() => {
    if (!gpuMode()) return;
    const ids = selectedSourceIds();
    if (!ids || ids.size === 0) return;
    const names = Array.from(ids)
      .map((id) => sourcesStore.getSourceName(id))
      .filter((n): n is string => !!n);
    if (names.length === 0) return;
    // Ref-counted union subscription so a co-mounted live timeseries (which subscribes its own channels) can
    // share this single socket; release (not stop) on cleanup so unmounting the map never kills its stream.
    // The two SGP wind channels are not in LIVE_STREAM_CHANNELS and are added here because boat wind reads the
    // SGP solution (see LIVE_BOAT_WIND_FIELDS below); two extra columns is negligible on the wire.
    arrowStreamClient.subscribe('live-map', names, [...LIVE_STREAM_CHANNELS, 'Tws_sgp_kph', 'Twd_sgp_deg']);
  });
  onCleanup(() => {
    try { arrowStreamClient.release('live-map'); } catch { /* noop */ }
  });

  const getStoredOverlayState = (key: string, defaultValue: boolean) => {
    try {
      const mapOverlays = persistantStore.mapOverlays?.();
      if (mapOverlays && mapOverlays[key] !== undefined) return mapOverlays[key];
      const stored = localStorage.getItem(`overlay_${key}`);
      if (stored === null) return defaultValue;
      return JSON.parse(stored);
    } catch {
      return defaultValue;
    }
  };

  const setStoredOverlayState = (key: string, value: boolean) => {
    try {
      const currentOverlays = persistantStore.mapOverlays?.() || {};
      persistantStore.setMapOverlays?.({ ...currentOverlays, [key]: value });
      localStorage.setItem(`overlay_${key}`, JSON.stringify(value));
    } catch (e) {
      logWarn(`LiveMapContainer: overlay store failed for ${key}:`, e);
    }
  };

  const [tracksEnabled, setTracksEnabledBase] = createSignal(getStoredOverlayState("tracks", true));
  const setTracksEnabled = (value: boolean | ((v: boolean) => boolean)) => {
    const newValue = typeof value === "function" ? value(tracksEnabled()) : value;
    setTracksEnabledBase(newValue);
    setStoredOverlayState("tracks", newValue);
    if (!newValue && badAirEnabled()) {
      setBadAirEnabledBase(false);
      setStoredOverlayState("bad-air", false);
    }
  };

  const [badAirEnabled, setBadAirEnabledBase] = createSignal(getStoredOverlayState("bad-air", false));
  const setBadAirEnabled = (value: boolean | ((v: boolean) => boolean)) => {
    const newValue = typeof value === "function" ? value(badAirEnabled()) : value;
    if (newValue && !tracksEnabled()) return;
    setBadAirEnabledBase(newValue);
    setStoredOverlayState("bad-air", newValue);
  };

  const [markWindEnabled, setMarkWindEnabledBase] = createSignal(getStoredOverlayState("mark-wind", false));
  const setMarkWindEnabled = (value: boolean | ((v: boolean) => boolean)) => {
    const newValue = typeof value === "function" ? value(markWindEnabled()) : value;
    setMarkWindEnabledBase(newValue);
    setStoredOverlayState("mark-wind", newValue);
  };

  // Boat wind feeds the wind field alongside the marks, and puts a TWS arrow on each moving boat. Only meaningful
  // while the wind overlay itself is on, so the setter is gated on it the way bad-air is gated on tracks.
  // OPT-IN (default off): the field is interpolated from the MARKS alone until the user turns this on.
  const [boatWindEnabled, setBoatWindEnabledBase] = createSignal(getStoredOverlayState("boat-wind", false));
  const setBoatWindEnabled = (value: boolean | ((v: boolean) => boolean)) => {
    const newValue = typeof value === "function" ? value(boatWindEnabled()) : value;
    if (newValue && !markWindEnabled()) return;
    setBoatWindEnabledBase(newValue);
    setStoredOverlayState("boat-wind", newValue);
  };

  const [twaTackEnabled, setTwaTackEnabledBase] = createSignal(getStoredOverlayState("twa-tack", true));
  const setTwaTackEnabled = (value: boolean | ((v: boolean) => boolean)) => {
    const newValue = typeof value === "function" ? value(twaTackEnabled()) : value;
    setTwaTackEnabledBase(newValue);
    setStoredOverlayState("twa-tack", newValue);
  };

  const [boundariesEnabled, setBoundariesEnabledBase] = createSignal(getStoredOverlayState("boundaries", true));
  const setBoundariesEnabled = (value: boolean | ((v: boolean) => boolean)) => {
    const newValue = typeof value === "function" ? value(boundariesEnabled()) : value;
    setBoundariesEnabledBase(newValue);
    setStoredOverlayState("boundaries", newValue);
  };

  const [maneuversEnabled, setManeuversEnabledBase] = createSignal(getStoredOverlayState("maneuvers", true));
  const setManeuversEnabled = (value: boolean | ((v: boolean) => boolean)) => {
    const newValue = typeof value === "function" ? value(maneuversEnabled()) : value;
    setManeuversEnabledBase(newValue);
    setStoredOverlayState("maneuvers", newValue);
  };

  const syncOverlayFromPersistentSettings = (
    key: string,
    current: () => boolean,
    setCurrent: (value: boolean) => void
  ) => {
    const mapOverlays = persistantStore.mapOverlays?.();
    const storedValue = mapOverlays?.[key];
    if (typeof storedValue === "boolean" && current() !== storedValue) {
      setCurrent(storedValue);
    }
  };

  createEffect(() => {
    syncOverlayFromPersistentSettings("tracks", tracksEnabled, setTracksEnabledBase);
    syncOverlayFromPersistentSettings("maneuvers", maneuversEnabled, setManeuversEnabledBase);
    syncOverlayFromPersistentSettings("boundaries", boundariesEnabled, setBoundariesEnabledBase);
    syncOverlayFromPersistentSettings("mark-wind", markWindEnabled, setMarkWindEnabledBase);
    syncOverlayFromPersistentSettings("boat-wind", boatWindEnabled, setBoatWindEnabledBase);
    syncOverlayFromPersistentSettings("twa-tack", twaTackEnabled, setTwaTackEnabledBase);

    const mapOverlays = persistantStore.mapOverlays?.();
    const badAirStoredValue = mapOverlays?.["bad-air"];
    if (typeof badAirStoredValue === "boolean" && tracksEnabled() && badAirEnabled() !== badAirStoredValue) {
      setBadAirEnabledBase(badAirStoredValue);
    }
  });

  const [overlaysInitialized, setOverlaysInitialized] = createSignal(false);
  const [mapDimensions, setMapDimensions] = createStore({ width: 1400, height: 900 });
  const [isInSplitView, setIsInSplitView] = createSignal(false);
  const [isHoveringTimeline, setIsHoveringTimeline] = createSignal(false);
  const [values, setValues] = createSignal<any[]>([]);
  const [lastLiveMapFrameAt, setLastLiveMapFrameAt] = createSignal<number | null>(null);
  const [isProgrammaticMovement, setIsProgrammaticMovement] = createSignal(false);
  // LiveTrackLayer reads streamingStore directly (incrementalMapUpdates=false) — bump frame clock for status badge
  createEffect(() => {
    if (!isPlaying()) return;
    trackLiveDataChange();
    void smoothPlaybackTimeForTrack();
    const ids = selectedSourceIds();
    if (ids.size === 0) return;
    const latest = gpuMode()
      ? liveColumnarStore.latestTs(selectedFrameIds())
      : streamingStore.getLatestTimestamp(ids);
    if (latest !== null) {
      setLastLiveMapFrameAt(Date.now());
    }
  });

  createEffect(() => {
    const checkSplitView = () => {
      const splitViewExists =
        document.querySelector(".split-view-content") !== null ||
        document.querySelector(".split-panel") !== null;
      untrack(() => {
        if (splitViewExists !== isInSplitView()) setIsInSplitView(splitViewExists);
      });
    };
    checkSplitView();
    const interval = setInterval(checkSplitView, 1000);
    // onCleanup, not a returned function: Solid ignores a createEffect return value, so the
    // returned disposer was never called and each effect run leaked its interval.
    onCleanup(() => clearInterval(interval));
  });

  createEffect(() => {
    if (isInSplitView() && map()) {
      setTimeout(() => triggerMapResize(), 100);
    }
  });

  let lastLiveMapSourcesKey = "";
  let hasPerformedInitialFit = false;
  let hasAppliedInitialBearing = false;
  let initialFitResizeScheduled = false;
  createEffect(() => {
    const key = Array.from(selectedSourceIds())
      .sort((a, b) => a - b)
      .join(",");
    if (key !== lastLiveMapSourcesKey) {
      // Reset only when switching between two different non-empty selections.
      // Avoid clearing a successful fit when the key catches up after async source restore.
      if (lastLiveMapSourcesKey !== "" && key !== "" && key !== lastLiveMapSourcesKey) {
        hasPerformedInitialFit = false;
        hasAppliedInitialBearing = false;
        setInitialFitComplete(false);
        initialFitResizeScheduled = false;
      }
      lastLiveMapSourcesKey = key;
      setLastLiveMapFrameAt(null);
    }
  });

  const computedValues = createMemo(() => values());

  const samplingFrequency = createMemo(() => {
    const data = computedValues();
    if (data && data.length >= 2) {
      const last = data[data.length - 1];
      const prev = data[data.length - 2];
      if (last?.Datetime && prev?.Datetime) {
        const intervalMs = new Date(last.Datetime).getTime() - new Date(prev.Datetime).getTime();
        if (intervalMs > 0 && intervalMs < 60000) return 1000 / intervalMs;
      }
    }
    return 1;
  });

  /** Densify history seed at 0.2s; live tail commits every boat clock tick (~30fps). */
  const effectivePlaybackTime = createMemo(() => {
    const tw = timeWindow();
    if (tw === 0 || !isPlaying()) return null;
    return smoothPlaybackTimeForTrack();
  });

  const trackLayerPlaybackTime = createMemo(() => {
    const tw = timeWindow();
    if (tw === 0 || !isPlaying()) return null;
    if (isPlaying()) void smoothPlaybackTimeForBoat();
    return smoothPlaybackTimeForBoat();
  });

  /** Rolling window for map tracks — never 0 (maneuvers window can sync timeWindow=0 cross-tab). */
  const liveMapReplayWindowMin = createMemo(() => {
    const tw = Number(timeWindow());
    return tw > 0 ? tw : LIVE_MAP_DEFAULT_TIME_WINDOW_MIN;
  });

  /**
   * Backfill retained history from parquet so the map can show more than this session streamed.
   *
   * The columnar rings only ever fill forward from the moment the page subscribed, and a `reset`
   * frame (reconnect / session boundary) drops a source's ring entirely and restarts it from a
   * ~2 minute snapshot. So "how far back can the map look?" is not the ring's capacity, it is
   * however long the socket has been quiet — which is why an hour of streaming still showed only
   * minutes, with holes. `hydrateSourceBack` fills behind whatever is retained.
   *
   * Runs while FOLLOWING as well as while paused. It was gated to paused-only on the reasoning that
   * chasing the live edge never looks behind it, which is wrong the moment the time-window dial is
   * widened: the window starts at `playhead - window`, so asking for 30 minutes on a session
   * holding two means 28 minutes nobody has. Widening the window is the most common way to need
   * this, and it was the one case the gate excluded.
   *
   * The service handles coverage, supersession and aborts, so re-running per tick is cheap: an
   * already-requested span returns without a request.
   */
  createEffect(() => {
    if (!gpuMode()) return;
    if (config.LIVE_MAP_SCRUB === false) return;

    const windowMin = liveMapReplayWindowMin();
    const playhead = selectedTime();
    const playheadMs = playhead instanceof Date ? playhead.getTime() : NaN;
    if (!isLiveReplayPlayheadReady(playheadMs)) return;

    const windowStartMs = playheadMs - windowMin * 60 * 1000;
    const styles = replaySourceStyles();

    for (const id of untrack(selectedSourceIds)) {
      const sourceName = styles.get(id)?.sourceName;
      if (!sourceName) continue;
      const frameSourceId = liveColumnarStore.sourceId(sourceName);
      if (frameSourceId === undefined) continue; // not streamed this session -> no ring to extend
      void hydrateSourceBack({
        sourceName,
        frameSourceId,
        windowStartMs,
        windowEndMs: playheadMs,
      }).catch((err) => {
        logWarn("LiveMapContainer: hydrate failed", { sourceName, err });
      });
    }
  });

  /** Slow lane: legacy streamingStore gap backfill (boats + tracks). Gated off in gpuMode — the columnar
   *  stream delivers deltas + snapshots directly, so there's no gap to backfill (Phase D4). */
  createEffect(() => {
    if (gpuMode()) return;
    if (!isPlaying()) return;
    const ids = selectedSourceIds();
    if (ids.size === 0) return;
    void liveConfigStore.activeCadence();

    const runRefresh = () => {
      // Skip a tick while the user is waiting on a page load: this is a /data/merged fetch per
      // selected source and it competes with the in-flight import. Nothing to un-stamp — the next
      // interval tick picks it up. See utils/renderYield.ts.
      if (isRenderYieldActive()) return;
      const targetMs = getLiveDisplayTargetMs(ids);
      if (!isLiveReplayPlayheadReady(targetMs)) return;
      const cadence = liveConfigStore.activeCadence();
      if (cadence === 'idle') {
        void streamingStore.refreshLiveMapTracksFromRedis(ids, Date.now());
      } else {
        void streamingStore.backfillLiveMapGapFromRedis(ids, Date.now());
      }
    };

    runRefresh();
    const intervalMs = liveConfigStore.trackRefreshIntervalMs();
    const timerId = window.setInterval(runRefresh, intervalMs);
    // onCleanup, not a returned function: Solid ignores a createEffect return value. This effect
    // re-runs on isPlaying / selectedSourceIds / cadence changes — all of which change repeatedly
    // during a live session — so the returned disposer never firing meant every re-run stacked
    // another 1s timer, each fetching /data/merged once per selected source. Requests grew without
    // bound the longer the live map stayed open.
    onCleanup(() => window.clearInterval(timerId));
  });

  /** sourceId → name/color (stable between frame ticks). */
  const replaySourceStyles = createMemo(() => {
    if (!sourcesStore.isReady()) return new Map();
    return buildLiveReplaySourceStyles(
      sourcesStore.sources(),
      (name) => sourcesStore.getSourceColor(name)
    );
  });

  /**
   * Names of the boats currently drawn, for the alarm marker filter.
   *
   * The Alarms page lists the whole fleet's alarms; the map draws the boats you selected. Without
   * this the layer placed a marker for every alarm the page held, including boats the map is not
   * showing.
   */
  const selectedSourceNames = createMemo(() => {
    const styles = replaySourceStyles();
    const ids = selectedSourceIds();
    const out: string[] = [];
    for (const id of ids) {
      const name = styles.get(id)?.sourceName || sourcesStore.getSourceName(id);
      if (name) out.push(name);
    }
    // NAMES WE CANNOT RESOLVE YET ARE NOT A SELECTION OF NOBODY. `replaySourceStyles` is an empty
    // Map until `sourcesStore.isReady()`, so early on every id here resolves to nothing - and an
    // empty-but-present list tells the layer to allow no boat at all, hiding every marker. Null
    // means "no opinion", the same convention the track window uses. An empty `ids` is different:
    // that is a real selection of nobody, and there are no tracks to pin a marker to either.
    if (out.length === 0 && ids.size > 0) {return null;}
    return out;
  });

  /**
   * A highlight must not outlive the boat it points at: deselecting a highlighted boat would
   * otherwise leave every remaining track dimmed for an emphasis with nothing to emphasise.
   */
  createEffect(() => {
    const names = selectedSourceNames();
    // Null is "cannot name the selection yet", not "nothing is selected". Pruning against it would
    // drop every highlight during the window before sourcesStore is ready.
    if (names) retainHighlightedSources(names);
  });

  /** Selected sources → the server's stream-assigned frame ids, resolved BY NAME via the arrow source
   *  registry (non-streamed sources dropped). Empty until the registry populates. */
  const selectedFrameIds = (): Set<number> => {
    const styles = replaySourceStyles();
    const out = new Set<number>();
    for (const id of selectedSourceIds()) {
      const name = styles.get(id)?.sourceName;
      const fid = name ? liveColumnarStore.sourceId(name) : undefined;
      if (fid !== undefined) out.add(fid);
    }
    return out;
  };

  /** Reactive "live data changed" trigger: the columnar store's epoch in gpuMode (streamingStore is idle
   *  there — Phase D4), else the legacy streamingStore append/history versions. */
  const trackLiveDataChange = (): void => {
    if (gpuMode()) {
      void liveColumnarStore.epoch();
    } else {
      void streamingStore.getLiveDataAppendVersion()();
      void streamingStore.getHistoricalDataVersion()();
    }
  };

  /** One real boat point per source per playhead tick — shared by boat + track layers. */
  const replayFramePoints = createMemo(() => {
    void streamingStore.getHistoricalDataVersion()();
    void streamingStore.getTrackDataVersion()();
    void liveMapReplayWindowMin();
    void smoothPlaybackTimeForBoat();
    const playhead = smoothPlaybackTimeForBoat();
    const selected = selectedSourceIds();
    const catalog = streamingStore.getLiveRenderData(selected, { shallow: true });
    return buildLiveReplayFramePoints(selected, catalog, playhead);
  });

  const trackData = createMemo(() => {
    const v = computedValues();
    return Array.isArray(v) ? [...v] : v;
  });

  const liveFrameSnapshot = createMemo(() => {
    void streamingStore.getLiveDataAppendVersion()();
    void streamingStore.getHistoricalDataVersion()();
    const tw = timeWindow();
    // PAUSED MUST MEAN PAUSED. This used to be `tw === 0 || !isPlaying() ? null : smooth…`, so the
    // moment the clock stopped `effectiveTime` went null and `endMs` below fell back to
    // `latestTs(...)` — the LIVE EDGE. Every overlay fed by this memo (wind, bad-air, LiveBspTable,
    // the per-boat wind arrows) therefore kept showing live values on a map the user had paused,
    // and scrubbing back moved the playhead while the overlays stayed pinned to the feed.
    // `getDisplayWindowReferenceTime()` is the playback store's own answer to "what time is on
    // screen": the smooth boat clock while playing, `selectedTime()` while paused.
    const effectiveTime = tw === 0 ? null : getDisplayWindowReferenceTime();
    if (isPlaying()) void smoothPlaybackTimeForBoat();
    const selected = selectedSourceIds();
    let filteredMap: Map<number, any[]>;
    if (gpuMode()) {
      // GPU path: window rows from the columnar store (overlays/wind/bad-air), no legacy streamingStore.
      // Resolve each selected source to the server's streamed source id by NAME (arrow source_registry); skip
      // sources the server is not streaming this session so overlays/table never pick up another boat's rows
      // via a numeric source-id collision (server ids are stream-assigned indices, not the selection ids).
      void liveColumnarStore.epoch();
      const styles = replaySourceStyles();
      const frameIdBySelected = new Map<number, number>();
      for (const id of selected) {
        const name = styles.get(id)?.sourceName;
        const fid = name ? liveColumnarStore.sourceId(name) : undefined;
        if (fid !== undefined) frameIdBySelected.set(id, fid);
      }
      const endMs = effectiveTime instanceof Date
        ? effectiveTime.getTime()
        : (liveColumnarStore.latestTs(new Set(frameIdBySelected.values())) ?? Date.now());
      const startMs = endMs - liveMapReplayWindowMin() * 60 * 1000;
      filteredMap = new Map<number, any[]>();
      for (const [selId, fid] of frameIdBySelected) {
        const rows = liveColumnarStore.windowRows(fid, startMs, endMs);
        if (!rows.length) continue;
        // windowRows carries only { timestamp, Datetime, ...channels } — no source_id. Overlay consumers
        // (bad-air, wind) filter/group by source_id, so stamp the SELECTION id onto each row. Without this
        // the bad-air overlay's `selected.has(row.source_id)` filter drops every point (rows are fresh copies,
        // safe to tag).
        for (const r of rows) r.source_id = selId;
        filteredMap.set(selId, rows);
      }
    } else {
      filteredMap = streamingStore.getFilteredData(
        selected,
        liveReplayFilteredDataOptions(effectiveTime, liveMapReplayWindowMin())
      );
    }
    return buildLiveFrameSnapshot(filteredMap, effectiveTime, selected, {
      latName: latName(),
      lngName: lngName(),
      twsName: twsName(),
      twdName: twdName(),
    });
  });

  const windData = createMemo(() => liveFrameSnapshot().wind);
  const overlayLiveData = createMemo(() => liveFrameSnapshot().overlayData);

  /**
   * Averaged wind from the boats that are actually sailing — fed to the wind-field overlay and drawn as a TWS
   * arrow on each qualifying boat.
   *
   * Wind comes from the SGP fused solution (`Tws_sgp_kph`/`Twd_sgp_deg`), which is why those two channels are
   * added to the live-map subscription above (LIVE_STREAM_CHANNELS does not carry them). The canonical
   * `Tws_kph`/`Twd_deg` are kept as the fallback: the stream server aliases them to the team-model solution
   * (processor.js WIND_CANONICAL_ALIAS), so a session with no SGP wind still produces boat samples instead of
   * silently producing none. Resolution happens per source in collectBoatWindSamples.
   *
   * Live rows are ALWAYS kph/deg regardless of class, so the names are fixed here rather than taken from
   * defaultChannelsStore (which resolves to _kts for non-GP50 classes and would read a channel the stream does
   * not carry).
   */
  const LIVE_BOAT_WIND_FIELDS = {
    lat: "Lat_dd",
    lng: "Lng_dd",
    tws: ["Tws_sgp_kph", "Tws_kph"],
    twd: ["Twd_sgp_deg", "Twd_deg"],
    bsp: "Bsp_kph",
  };

  // 1 Hz clock. The snapshot itself churns at frame rate, so the sampling is paced off this instead: a boat's
  // 20s-averaged wind cannot meaningfully change faster, and a new array every frame would push prop churn into
  // the overlay manager and the boat layers for nothing.
  const [boatWindClock, setBoatWindClock] = createSignal(0);
  onMount(() => {
    const timer = setInterval(() => setBoatWindClock((v) => v + 1), 1000);
    onCleanup(() => clearInterval(timer));
  });

  const boatWindSamples = createMemo(() => {
    void boatWindClock();
    if (!markWindEnabled() || !boatWindEnabled()) return [];
    // Everything below is read UNTRACKED: liveFrameSnapshot subscribes to the columnar epoch / append version,
    // which would defeat the 1 Hz pacing above.
    return untrack(() => {
      const playhead = smoothPlaybackTimeForBoat();
      const endMs = playhead instanceof Date ? playhead.getTime() : undefined;
      const styles = replaySourceStyles();
      return collectBoatWindSamples(liveFrameSnapshot().filteredMap, LIVE_BOAT_WIND_FIELDS, {
        endMs,
        nameById: (id) => styles.get(id)?.sourceName,
      });
    });
  });

  /**
   * The same rows as a TRAIL of time-tagged readings for the wind field to advect downwind. Same 1 Hz pacing and
   * same untracked reads as boatWindSamples above — liveFrameSnapshot already holds several minutes of rows.
   */
  const boatWindHistory = createMemo(() => {
    void boatWindClock();
    if (!markWindEnabled() || !boatWindEnabled()) return [];
    return untrack(() => {
      const playhead = smoothPlaybackTimeForBoat();
      const endMs = playhead instanceof Date ? playhead.getTime() : undefined;
      return collectBoatHistorySamples(liveFrameSnapshot().filteredMap, LIVE_BOAT_WIND_FIELDS, { endMs });
    });
  });

  /** Boat wind keyed by boat name, for the boat layers (which resolve boats by name, not selection id). */
  const boatWindByName = createMemo(() => {
    const out = new Map<string, { tws: number; twd: number }>();
    for (const s of boatWindSamples()) {
      if (s.name) out.set(s.name, { tws: s.tws, twd: s.twd });
    }
    return out;
  });

  const WIND_ROTATION_STORAGE_KEY = "map_wind_rotation_preference";

  const getSavedWindRotationIndex = (): number => {
    try {
      const saved = localStorage.getItem(WIND_ROTATION_STORAGE_KEY);
      if (saved !== null) {
        const v = parseInt(saved, 10);
        if (v >= 0 && v <= 4) return v;
      }
    } catch {
      /* ignore */
    }
    return 0;
  };

  const getBearingFromWindRotationIndex = (twd: number | undefined): number => {
    if (twd === undefined) return 0;
    const index = getSavedWindRotationIndex();
    let bearing = 0;
    if (index === 1) bearing = twd;
    else if (index === 2) bearing = twd + 90;
    else if (index === 3) bearing = twd + 180;
    else if (index === 4) bearing = twd + 270;
    while (bearing > 180) bearing -= 360;
    while (bearing < -180) bearing += 360;
    return bearing;
  };

  /** North-up (index 0) needs no TWD; wind-aligned modes need live TWD before rotating. */
  const canApplySavedBearing = (twd: number | undefined): boolean => {
    return getSavedWindRotationIndex() === 0 || twd !== undefined;
  };

  const [initialFitComplete, setInitialFitComplete] = createSignal(false);

  /** Apply saved wind-arrow rotation (north / wind-up / ±90°) after programmatic fitBounds. */
  const restoreSavedBearing = () => {
    if (hasAppliedInitialBearing) return;
    const currentMap = map();
    if (!currentMap) return;

    const twd = windData()?.twd;
    if (!canApplySavedBearing(twd)) {
      logDebug("LiveMapContainer: deferring bearing restore until TWD is available", {
        rotationIndex: getSavedWindRotationIndex(),
      });
      return;
    }

    const apply = () => {
      const m = map();
      if (!m || hasAppliedInitialBearing) return;
      const currentTwd = windData()?.twd;
      if (!canApplySavedBearing(currentTwd)) return;
      const bearing = getBearingFromWindRotationIndex(currentTwd);
      m.setBearing(bearing);
      hasAppliedInitialBearing = true;
      logDebug("LiveMapContainer: restored bearing from wind rotation preference", {
        rotationIndex: getSavedWindRotationIndex(),
        twd: currentTwd,
        bearing,
      });
    };
    currentMap.once("moveend", apply);
    setTimeout(apply, 150);
  };

  createEffect(() => {
    const currentMap = map();
    const wind = windData();
    trackLiveDataChange();
    if (hasAppliedInitialBearing || !currentMap || !initialFitComplete()) return;
    if (!canApplySavedBearing(wind?.twd)) return;
    restoreSavedBearing();
  });

  /** Retry bearing restore while TWD may still be loading on first open. */
  createEffect(() => {
    if (!initialFitComplete() || hasAppliedInitialBearing) return;
    const retryMs = [800, 2000, 4000];
    const timers = retryMs.map((ms) => setTimeout(() => restoreSavedBearing(), ms));
    onCleanup(() => timers.forEach((id) => clearTimeout(id)));
  });

  let lastMapContextKey = "";
  createEffect(() => {
    const className = persistantStore.selectedClassName?.() ?? "";
    const projectId = persistantStore.selectedProjectId?.() ?? "";
    const datasetId = persistantStore.selectedDatasetId?.() ?? "";
    const key = `${className}_${projectId}_${datasetId}`;
    if (key === lastMapContextKey) return;
    const prev = lastMapContextKey;
    lastMapContextKey = key;
    if (prev !== "") {
      hasPerformedInitialFit = false;
      hasAppliedInitialBearing = false;
      setInitialFitComplete(false);
      initialFitResizeScheduled = false;
    }
  });

  const expandBoundsIfDegenerate = (
    bounds: [[number, number], [number, number]]
  ): [[number, number], [number, number]] => {
    const [[minLng, minLat], [maxLng, maxLat]] = bounds;
    if (Math.abs(maxLng - minLng) < 0.0001 && Math.abs(maxLat - minLat) < 0.0001) {
      const pad = 0.025;
      return [
        [minLng - pad, minLat - pad],
        [maxLng + pad, maxLat + pad],
      ];
    }
    return bounds;
  };

  const isMapAtPlaceholderView = (m: any): boolean => {
    if (!m) return true;
    try {
      const c = m.getCenter();
      const z = m.getZoom();
      const isLegacyNyc =
        Math.abs(c.lng - LEGACY_NYC_CENTER.lng) < 0.15 &&
        Math.abs(c.lat - LEGACY_NYC_CENTER.lat) < 0.15 &&
        Math.abs(z - 14) < 1;
      const isWorldPlaceholder =
        Math.abs(c.lng - LIVE_MAP_PLACEHOLDER_CENTER[0]) < 2 &&
        Math.abs(c.lat - LIVE_MAP_PLACEHOLDER_CENTER[1]) < 8 &&
        z < 5;
      return isLegacyNyc || isWorldPlaceholder;
    } catch {
      return true;
    }
  };

  const mapNeedsBoatFit = (m: any): boolean => !hasPerformedInitialFit || isMapAtPlaceholderView(m);

  const calculateBounds = (data: any[]): [[number, number], [number, number]] | null => {
    if (!data?.length) return null;
    const latField = latName();
    const lngField = lngName();
    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity;
    let valid = 0;
    data.forEach((point: any) => {
      const resolved = livePointCoords(point);
      const lat = resolved?.lat ?? point[latField] ?? point.Lat_dd ?? point.lat;
      const lng = resolved?.lng ?? point[lngField] ?? point.Lng_dd ?? point.lng;
      if (lat != null && lng != null) {
        const la = Number(lat);
        const ln = Number(lng);
        if (!isNaN(la) && !isNaN(ln)) {
          minLng = Math.min(minLng, ln);
          maxLng = Math.max(maxLng, ln);
          minLat = Math.min(minLat, la);
          maxLat = Math.max(maxLat, la);
          valid++;
        }
      }
    });
    if (valid === 0) return null;
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  };

  const fitBoundsPreservingOrientation = (
    m: any,
    bounds: [[number, number], [number, number]],
    options?: { padding?: number; maxZoom?: number; duration?: number }
  ) => {
    const savedBearing = m.getBearing();
    const savedPitch = m.getPitch();
    m.fitBounds(bounds, { padding: 50, maxZoom: 16, duration: 0, ...options });
    requestAnimationFrame(() => {
      m.setBearing(savedBearing);
      m.setPitch(savedPitch);
    });
  };

  let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;
  // Set in onCleanup: the map signal keeps pointing at the removed Mapbox instance, so debounced
  // resizes that survive unmount would call resize() on a destroyed map and blow up inside mapbox.
  let isDisposed = false;

  const triggerMapResize = () => {
    if (isDisposed) return;
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      if (isDisposed) return;
      const currentMap = map();
      const container = mapContainer();
      if (currentMap && !currentMap._removed && container?.isConnected) {
        try {
          currentMap.resize();
        } catch {
          return;
        }
        const canvasContainer = container.querySelector(".mapboxgl-canvas-container") as HTMLElement | null;
        if (canvasContainer) {
          setMapDimensions({
            width: canvasContainer.offsetWidth || container.offsetWidth || 1400,
            height: canvasContainer.offsetHeight || container.offsetHeight || 900,
          });
        }
      }
    }, 100);
  };

  const setupResizeObservers = () => {
    const container = mapContainer();
    if (!container) return;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => triggerMapResize());
      resizeObserver.observe(container);
      const splitPanel = container.closest(".split-panel");
      if (splitPanel) resizeObserver.observe(splitPanel);
    }
    const sidebar = document.querySelector(".sidebar");
    if (sidebar && window.MutationObserver) {
      mutationObserver = new MutationObserver(() => triggerMapResize());
      mutationObserver.observe(sidebar, { attributes: true, attributeFilter: ["class"] });
    }
  };

  let lastHandledSignature: string | null = null;
  let initialFitTimer: ReturnType<typeof setTimeout> | null = null;
  let mapInitStarted = false;
  let mapInitWaitTimer: ReturnType<typeof setTimeout> | null = null;

  const performInitialFit = (
    currentMap: any,
    bounds: [[number, number], [number, number]],
    pointCount: number
  ) => {
    setIsProgrammaticMovement(true);
    try {
      currentMap.resize();
    } catch {
      /* ignore */
    }
    const expanded = expandBoundsIfDegenerate(bounds);
    fitBoundsPreservingOrientation(currentMap, expanded, { duration: 0, padding: 80, maxZoom: 16 });

    const markFitComplete = () => {
      if (!isMapAtPlaceholderView(currentMap)) {
        hasPerformedInitialFit = true;
        setInitialFitComplete(true);
        logDebug("LiveMapContainer: fitted map to live GPS bounds", {
          pointCount,
          bounds: expanded,
        });
      } else {
        hasPerformedInitialFit = false;
        setInitialFitComplete(false);
        logDebug("LiveMapContainer: map still at placeholder view after fit, will retry", {
          pointCount,
        });
      }
      setIsProgrammaticMovement(false);
    };
    setTimeout(markFitComplete, 400);
    setTimeout(markFitComplete, 1500);
  };

  const runWhenMapReadyForFit = (currentMap: any, callback: () => void) => {
    const run = () => {
      try {
        currentMap.resize();
      } catch {
        /* ignore */
      }
      callback();
    };
    if (currentMap.loaded()) {
      requestAnimationFrame(() => requestAnimationFrame(run));
      return;
    }
    logDebug("LiveMapContainer: map not loaded yet, deferring initial fit to load event");
    currentMap.once("load", () => requestAnimationFrame(() => requestAnimationFrame(run)));
  };

  const tryFitMapToGpsPoints = (points: any[]) => {
    if (points.length === 0) return;
    const currentMap = map();
    if (!currentMap || !mapNeedsBoatFit(currentMap)) return;
    const bounds = calculateBounds(points);
    if (!bounds) {
      logDebug("LiveMapContainer: initial fit skipped — no valid GPS in points", {
        pointCount: points.length,
      });
      return;
    }

    runWhenMapReadyForFit(currentMap, () => {
      const m = map();
      if (!m || !mapNeedsBoatFit(m)) return;
      const fitBounds = calculateBounds(points);
      if (!fitBounds) return;
      performInitialFit(m, fitBounds, points.length);
    });
  };

  const collectLiveGpsPointsForBounds = (ids: Set<number>): any[] => {
    const points: any[] = [];
    const seen = new Set<string>();

    const addPoint = (point: any) => {
      if (!point) return;
      const coords = livePointCoords(point);
      if (!coords) return;
      const key = `${coords.lat.toFixed(6)}:${coords.lng.toFixed(6)}`;
      if (seen.has(key)) return;
      seen.add(key);
      points.push(point);
    };

    if (gpuMode()) {
      // Phase D4: collect recent GPS across the streamed boats from the columnar store (resolved by name →
      // frame id), for the initial fit + refit. No streamingStore in gpuMode.
      const styles = replaySourceStyles();
      for (const id of ids) {
        const name = styles.get(id)?.sourceName;
        const fid = name ? liveColumnarStore.sourceId(name) : undefined;
        if (fid === undefined) continue;
        const coords = liveColumnarStore.trackCoords(fid, 0, Number.MAX_SAFE_INTEGER);
        for (const c of coords) addPoint({ Lat_dd: c[1], Lng_dd: c[0] });
      }
      return points;
    }

    // Prefer latest visible boat positions (same samples as LiveMultiBoatLayer).
    for (const point of replayFramePoints().values()) {
      addPoint(point);
    }
    if (points.length > 0) return points;

    const trackMap = streamingStore.getLiveTrackData(ids, { shallow: true });
    for (const arr of trackMap.values()) {
      if (arr?.length) arr.forEach(addPoint);
    }
    if (points.length > 0) return points;

    const renderMap = streamingStore.getLiveRenderData(ids, { shallow: true });
    for (const arr of renderMap.values()) {
      if (arr?.length) arr.forEach(addPoint);
    }
    if (points.length > 0) return points;

    const rawMap = streamingStore.getRawData(ids, { shallow: true });
    for (const arr of rawMap.values()) {
      if (arr?.length) arr.forEach(addPoint);
    }
    if (points.length > 0) return points;

    const filtered = trackData();
    if (filtered?.length) filtered.forEach(addPoint);

    return points;
  };

  const resolveInitialMapView = (ids: Set<number>): { center: [number, number]; zoom: number } => {
    const bounds = calculateBounds(collectLiveGpsPointsForBounds(ids));
    if (bounds) {
      return {
        center: [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2],
        zoom: 11,
      };
    }
    return { center: LIVE_MAP_PLACEHOLDER_CENTER, zoom: LIVE_MAP_PLACEHOLDER_ZOOM };
  };

  const startMapInit = () => {
    if (mapInitStarted || map()) return;
    mapInitStarted = true;
    if (mapInitWaitTimer) {
      clearTimeout(mapInitWaitTimer);
      mapInitWaitTimer = null;
    }
    const ids = selectedSourceIds();
    void initMap(resolveInitialMapView(ids));
  };

  /** Start map once boat GPS is available (avoids flashing NYC / wrong default center). */
  createEffect(() => {
    if (map() || mapInitStarted || !mapContainer()) return;
    const ids = selectedSourceIds();
    if (ids.size === 0) return;
    trackLiveDataChange();
    const points = collectLiveGpsPointsForBounds(ids);
    if (points.length > 0) startMapInit();
  });

  const retryFitIfStillAtPlaceholder = () => {
    const currentMap = map();
    const ids = selectedSourceIds();
    if (!currentMap || ids.size === 0 || !mapNeedsBoatFit(currentMap)) return;
    tryFitMapToGpsPoints(collectLiveGpsPointsForBounds(ids));
  };

  /** Keep fitting while the camera is still on the placeholder view. */
  createEffect(() => {
    const currentMap = map();
    const ids = selectedSourceIds();
    if (!currentMap || ids.size === 0 || !mapNeedsBoatFit(currentMap)) return;

    trackLiveDataChange();

    const points = collectLiveGpsPointsForBounds(ids);
    if (points.length === 0) return;

    tryFitMapToGpsPoints(points);
  });

  const handleDataUpdate = (payload: any) => {
    if (!Array.isArray(payload)) return;
    tryFitMapToGpsPoints(payload);
    let dataToProcess = payload;

    const twMin = Number(timeWindow());
    if (twMin > 0 && dataToProcess.length > 0) {
      const ref = getDisplayWindowReferenceTime();
      if (ref instanceof Date && !isNaN(ref.getTime())) {
        const windowMs = twMin * 60 * 1000;
        const startMs = ref.getTime() - windowMs;
        const endMs = ref.getTime();
        dataToProcess = dataToProcess.filter((p: any) => {
          const dt = p.Datetime;
          if (!dt) return false;
          const t = dt instanceof Date ? dt.getTime() : new Date(dt).getTime();
          return Number.isFinite(t) && t >= startMs && t <= endMs;
        });
      }
    }

    const sig =
      dataToProcess.length > 0
        ? `${dataToProcess.length}_${dataToProcess[0]?.Datetime}_${dataToProcess[dataToProcess.length - 1]?.Datetime}`
        : "0";
    if (sig === lastHandledSignature) return;
    lastHandledSignature = sig;

    setValues(dataToProcess);
    setLastLiveMapFrameAt(Date.now());
  };

  const initMap = async (initialView: { center: [number, number]; zoom: number }) => {
    try {
      const mapboxModule = await import("mapbox-gl");
      mapboxgl = mapboxModule.default || mapboxModule;
      if (!mapboxgl || !config.MAPBOX_TOKEN?.trim()) {
        logError("LiveMapContainer: mapbox unavailable or token missing");
        return;
      }
      mapboxgl.accessToken = config.MAPBOX_TOKEN;

      let attempts = 0;
      while (!mapContainer() && attempts < 50) {
        await new Promise((r) => setTimeout(r, 100));
        attempts++;
      }
      if (!mapContainer()) return;

      const savedStateKey = `mapState_${objectName}`;

      const container = mapContainer();
      while (container.firstChild) container.removeChild(container.firstChild);

      const selectedStyle =
        config.MAPBOX_STYLE?.trim() !== "" ? config.MAPBOX_STYLE : "mapbox://styles/mapbox/satellite-v9";

      const newMap = new mapboxgl.Map({
        container,
        style: selectedStyle,
        center: initialView.center,
        zoom: initialView.zoom,
        bearing: 0,
        pitch: 0,
        antialias: true,
        attributionControl: true,
      });

      setMap(newMap);
      window.map = newMap;

      try {
        newMap.addControl(new mapboxgl.AttributionControl({ compact: false }));
      } catch {
        /* ignore */
      }

      newMap.on("load", () => {
        setupResizeObservers();
        setTimeout(() => triggerMapResize(), 100);
        setTimeout(() => retryFitIfStillAtPlaceholder(), 200);
        setTimeout(() => retryFitIfStillAtPlaceholder(), 1000);
        setTimeout(() => retryFitIfStillAtPlaceholder(), 2500);
      });

      newMap.on("dblclick", (e: any) => {
        e.originalEvent?.stopPropagation();
        e.originalEvent?.preventDefault();
        const data = trackData();
        if (data?.length > 0) {
          const bounds = calculateBounds(data);
          if (bounds) {
            setIsProgrammaticMovement(true);
            fitBoundsPreservingOrientation(newMap, bounds, { duration: 1000 });
            setTimeout(() => setIsProgrammaticMovement(false), 1100);
          }
        }
      });

      newMap.doubleClickZoom.disable();
      newMap.on("zoom", () => setCurrentZoom(newMap.getZoom()));
      newMap.on("zoomend", () => {
        setCurrentZoom(newMap.getZoom());
      });
      setCurrentZoom(newMap.getZoom());

      newMap.on("moveend", () => {
        if (isProgrammaticMovement() || !hasPerformedInitialFit || isMapAtPlaceholderView(newMap)) return;
        const center = newMap.getCenter();
        try {
          localStorage.setItem(
            savedStateKey,
            JSON.stringify({
              center: [center.lng, center.lat],
              zoom: newMap.getZoom(),
              bearing: newMap.getBearing(),
            })
          );
        } catch {
          /* ignore */
        }
      });
    } catch (err) {
      logError("LiveMapContainer initMap:", err);
    }
  };

  createEffect(() => {
    const className = persistantStore.selectedClassName?.();
    if (!className) return;
    (async () => {
      try {
        const { huniDBStore } = await import("../../../store/huniDBStore");
        const cached = await huniDBStore.getObject(className, "zoom_settings");
        if (cached && typeof cached === "object") setZoomSettings(cached);
      } catch {
        setZoomSettings(null);
      }
    })();
  });

  onMount(async () => {
    void logPageLoad("LiveMapContainer.tsx", "Live Map", "Mounted");
    setHasChartsWithOwnFilters(false);
    initializeOverlays();
    setOverlaysInitialized(true);
    window.addEventListener("resize", triggerMapResize);
    mapInitWaitTimer = setTimeout(() => startMapInit(), LIVE_MAP_INIT_WAIT_MS);
    await (async () => {
      startPeriodicSync();
      syncSelectedTimeManual();
    })();
  });

  onCleanup(() => {
    isDisposed = true;
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = null;
    if (initialFitTimer) clearTimeout(initialFitTimer);
    if (mapInitWaitTimer) clearTimeout(mapInitWaitTimer);
    initialFitResizeScheduled = false;
    window.removeEventListener("resize", triggerMapResize);
    if (resizeObserver) resizeObserver.disconnect();
    if (mutationObserver) mutationObserver.disconnect();
    const currentMap = map();
    if (currentMap) currentMap.remove();
    if (window.map) delete window.map;
    setHasChartsWithOwnFilters(false);
  });

  const effectiveDataOverlay = (): string => {
    const n = objectName !== "map_default" ? objectName : "";
    return n || "default";
  };

  return (
    <Show when={hasShownMapOnce() || isSourcesReady()} fallback={<Loading message="Initializing live map..." />}>
      <div class="map-container live-map-container">
        <MapSettings
          sourceSelectionScope="liveMap"
          useIconTrigger={true}
          hideAllFilters={true}
          hideColorOptions={true}
          hideOverlayOptionsLabel={true}
          useUnfilteredOptions={true}
          colorOptions={["DEFAULT"]}
          dataSourcesOptions={dataSourcesOptionsMemo()}
          displayOptions={[
            { key: "overlay-tracks", label: "Tracks", type: "toggle", signal: [tracksEnabled, setTracksEnabled], values: [true] },
            { key: "overlay-maneuvers", label: "Maneuvers", type: "toggle", signal: [maneuversEnabled, setManeuversEnabled], values: [true] },
            { key: "overlay-boundaries", label: "Marks & Boundaries", type: "toggle", signal: [boundariesEnabled, setBoundariesEnabled], values: [true] },
            { key: "overlay-bad-air", label: "Bad Air", type: "toggle", signal: [badAirEnabled, setBadAirEnabled], values: [true] },
            { key: "overlay-mark-wind", label: "Mark Wind", type: "toggle", signal: [markWindEnabled, setMarkWindEnabled], values: [true] },
            { key: "overlay-boat-wind", label: "Boat Wind", type: "toggle", signal: [boatWindEnabled, setBoatWindEnabled], values: [true] },
            { key: "overlay-twa-tack", label: "TWA Bow/MHU", type: "toggle", signal: [twaTackEnabled, setTwaTackEnabled], values: [true] },
          ]}
          options={[]}
          selectedStatesTimeseries={[]}
          setSelectedStatesTimeseries={() => {}}
          raceOptions={[]}
          setRaceOptions={() => {}}
          legOptions={[]}
          setLegOptions={() => {}}
          gradeOptions={[]}
          setGradeOptions={() => {}}
          selectedRacesTimeseries={[]}
          setSelectedRacesTimeseries={() => {}}
          selectedRaces={[]}
          setSelectedRaces={() => {}}
          selectedLegsTimeseries={[]}
          setSelectedLegsTimeseries={() => {}}
          selectedLegs={[]}
          selectedGradesTimeseries={[]}
          toggleFilter={() => {}}
          toggleRaceFilter={() => {}}
          toggleLegFilter={() => {}}
          toggleGradeFilter={() => {}}
          isFleetMap={true}
        />

        <div class="map live-map-area">
          <div
            ref={setMapContainer}
            class="live-mapbox-target"
          />
          {/* These three used to carry a `!isInSplitView()` gate as well, so opening the live map
              beside another page silently removed them — including the status pill, which is the
              only thing reporting whether the transport is live, paused or stale. The toggles for
              the two tables stayed in Map Settings throughout, so switching one on did nothing and
              read as a fault rather than as a layout decision. Each now honours only its own
              condition, in split view exactly as full width. */}
          <Show when={twaTackEnabled()}>
            <div class="live-twa-tack-positioner">
              <LiveTwaTackTable />
            </div>
          </Show>
          <Show when={isLiveDefaultDataOverlay(effectiveDataOverlay())}>
            <div class="live-bsp-table-positioner">
              <LiveBspTable
                selectedSourceIds={() => selectedSourceIds()}
                filteredDataMap={() => liveFrameSnapshot().filteredMap}
              />
            </div>
          </Show>
          <div class="live-map-stream-status-positioner">
            <LiveMapStreamStatus
              selectedSourceIds={() => selectedSourceIds()}
              lastMapFrameAtMs={() => lastLiveMapFrameAt()}
            />
          </div>
        </div>

        <div
          class="map-timeline-section map-timeline-section-compact"
          onMouseEnter={() => setIsHoveringTimeline(true)}
          onMouseLeave={() => setIsHoveringTimeline(false)}
        >
          <div
            ref={(el) => (chartContainer = el)}
            class="chart-container chart-container-compact map-timeline-chart-wrap"
          >
            <LiveMapUPlotTimeSeries
              samplingFrequency={samplingFrequency()}
              onMapUpdate={handleDataUpdate}
              selectedSourceIds={selectedSourceIds()}
              incrementalMapUpdates={false}
              replayMode={true}
            />
          </div>
          <div class={`timeline-controls-overlay${isHoveringTimeline() ? " timeline-controls-overlay-visible" : ""}`}>
            <PlayPause position="timeline-overlay" allowFastFwd={true} allowTimeWindow={true} hideFullTimeWindow={false} />
          </div>
        </div>

        <Show when={map() && tracksEnabled()}>
          <Show
            when={gpuMode()}
            fallback={
              <>
                <LiveTrackLayer
                  map={map()}
                  selectedSourceIds={selectedSourceIds()}
                  pointRadius={4}
                  historicalData={computedValues()}
                  effectivePlaybackTime={trackLayerPlaybackTime()}
                  inSplitView={isInSplitView()}
                  replayMode={true}
                  framePoints={replayFramePoints()}
                  sourceStyles={replaySourceStyles()}
                  replayTimeWindowMin={liveMapReplayWindowMin()}
                />
                <Show when={map() && mapContainer() && showBoats()}>
                  <LiveMultiBoatLayer
                    key={`live-multi-boat-${showBoats()}`}
                    map={map()}
                    mapContainer={mapContainer()}
                    samplingFrequency={samplingFrequency()}
                    selectedSourceIds={selectedSourceIds()}
                    inSplitView={isInSplitView()}
                    replayMode={true}
                    framePoints={replayFramePoints()}
                    sourceStyles={replaySourceStyles()}
                    replayTimeWindowMin={liveMapReplayWindowMin()}
                    boatWindByName={boatWindByName()}
                  />
                </Show>
              </>
            }
          >
            {/* GPU/columnar path */}
            <GlTrackLayer
              map={map()}
              selectedSourceIds={selectedSourceIds()}
              sourceStyles={replaySourceStyles()}
              replayTimeWindowMin={liveMapReplayWindowMin()}
            />
            <Show when={map() && mapContainer() && showBoats()}>
              <GlBoatLayer
                map={map()}
                mapContainer={mapContainer()}
                selectedSourceIds={selectedSourceIds()}
                sourceStyles={replaySourceStyles()}
                boatWindByName={boatWindByName()}
              />
            </Show>
          </Show>
        </Show>

        <Show
          when={
            map() &&
            boundariesEnabled() &&
            persistantStore.selectedClassName?.() &&
            Number(persistantStore.selectedProjectId?.()) > 0
          }
        >
          <RaceCourseLayer
            key={`live-race-course-${boundariesEnabled()}`}
            map={map()}
            visible={true}
          />
        </Show>

        {/* Alarm markers. LiveMapContainer is a SEPARATE tree from MapContainer and shares none of
            its layers, so the live map needs its own mount. Gated only on the Alarms page being
            open; the page's "Show on map" checkbox is the second gate, via the marker store. */}
        <Show when={map()}>
          <AlarmsExploreMarkersLayer
            map={map()}
            trackData={computedValues()}
            enabled={!!persistantStore.alarmsExploreOpen?.()}
            selectedSources={selectedSourceNames()}
          />
        </Show>

        <Show when={map()}>
          <WindArrow
            map={map()}
            tws={windData().tws}
            twd={windData().twd}
            maptype="DEFAULT"
            trackData={computedValues()}
            selectedTime={selectedTime()}
            objectName={objectName}
          />
        </Show>

        <Show when={map() && overlaysInitialized() && mapContainer()}>
          <OverlayManager
            map={map()}
            mapContainer={mapContainer()}
            data={overlayLiveData()}
            liveMode={true}
            width={mapDimensions.width}
            height={mapDimensions.height}
            effectivePlaybackTime={effectivePlaybackTime()}
            samplingFrequency={samplingFrequency()}
            boatWindSamples={boatWindSamples()}
            boatWindHistory={boatWindHistory()}
            enabledStates={{
              "bad-air": { get: badAirEnabled, set: setBadAirEnabled },
              "mark-wind": { get: markWindEnabled, set: setMarkWindEnabled },
            }}
          />
        </Show>
      </div>
    </Show>
  );
}
